/**
 * Servizio API per comunicare con WordPress e recuperare le esperienze
 * Sostituisce le configurazioni hardcoded con dati dinamici dal database
 */

export interface WordPressExperience {
  id: number
  experience_id: string
  title: string
  description: string
  experience_type: '3d' | 'ar' | 'hologram' | 'video'
  model_type: string
  scale_x: number
  scale_y: number
  scale_z: number
  position_x: number
  position_y: number
  position_z: number
  rotation_x: number
  rotation_y: number
  rotation_z: number
  animation: 'yes' | 'no'
  repeat_animation: 'yes' | 'no'
  animation_text: string
  model_url: string
  image_url: string
  video_url: string
  active: boolean
  created_at: string
  updated_at: string
  animations?: WordPressAnimation[]
  custom_params?: WordPressCustomParam[]
  ar_config?: WordPressARConfig
  hologram_config?: WordPressHologramConfig
  envmap?: any // Oggetto envmap con pos_x, neg_x, pos_y, neg_y, pos_z, neg_z
}

export interface WordPressAnimation {
  id: number
  experience_id: string
  animation_name: string
  animation_order: number
}

export interface WordPressCustomParam {
  id: number
  experience_id: string
  param_name: string
  param_value: string
  param_type: 'string' | 'number' | 'boolean' | 'json' | 'url'
}

export interface WordPressARConfig {
  id: number
  experience_id: string
  target_name: string
  camera_position_x: number
  camera_position_y: number
  camera_position_z: number
  light_intensity: number
  center_image_url: string
  center_image_scale_x: number
  center_image_scale_y: number
  center_image_scale_z: number
  ar_type: string
  width?: number  // Larghezza del target/video
  height?: number // Altezza del target/video
}

export interface WordPressHologramConfig {
  id: number
  experience_id: string
  hologram_type: 'projection' | 'floating' | 'video'
  projection_surface: 'cylindrical' | 'flat' | 'air'
  video_name: string
  chroma_color_r: number
  chroma_color_g: number
  chroma_color_b: number
  ground_size_width: number
  ground_size_height: number
  rise_height: number
  autoplay: boolean
  loop_video: boolean
  brightness: string
  color_temperature: string
  ambient_light: boolean
  particle_effects: boolean
  sound_enabled: boolean
  depth: string
}

export interface WordPressAPIResponse<T> {
  success: boolean
  data?: T
  message?: string
  error?: string
}

// Interfacce specifiche per le risposte della nuova API
export interface WordPressExperiencesResponse {
  experiences: WordPressExperience[]
  total: number
  type: string
}

export interface WordPressExperienceResponse {
  experience: WordPressExperience
}

export interface WordPressSearchResponse {
  experiences: WordPressExperience[]
  total: number
  search_term: string
}

export interface WordPressHealthResponse {
  status: string
  database: string
  total_experiences: number
  type_counts: Array<{ experience_type: string; count: number }>
  timestamp: string
  wordpress_version: string
  plugin_version: string
}

class WordPressAPI {
  private baseUrl: string
  private nonce: string
  private cache: Map<string, { data: any; timestamp: number }> = new Map()
  private cacheDuration: number = 5 * 60 * 1000 // 5 minuti
  private isOnline: boolean = true

  constructor() {
    // Estrai l'URL base da meta tag o configurazione
    this.baseUrl = this.getBaseUrl()
    this.nonce = this.getNonce()
    
    // Test iniziale della connessione (non bloccante)
    setTimeout(() => {
      this.testConnection()
    }, 100)
  }

  private getBaseUrl(): string {
    // Prova a ottenere l'URL dal meta tag
    const metaTag = document.querySelector('meta[name="wordpress-api-url"]')
    if (metaTag) {
      return metaTag.getAttribute('content') || ''
    }

    // Fallback: URL fisso dell'API WordPress
    return 'https://iscag-ei.it/wp-content/museo-api.php'
  }

  private getNonce(): string {
    // Prova a ottenere il nonce dal meta tag
    const metaTag = document.querySelector('meta[name="wordpress-nonce"]')
    if (metaTag) {
      return metaTag.getAttribute('content') || ''
    }

    // Fallback: nonce vuoto (potrebbe causare errori di sicurezza)
    return ''
  }

  /**
   * Testa la connessione all'API WordPress
   */
  private async testConnection(): Promise<void> {
    try {
      console.log('🧪 Test connessione API WordPress...')
      console.log('🔗 URL API:', this.baseUrl)
      
      const response = await fetch(`${this.baseUrl}?action=experiences`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success && result.data) {
          this.isOnline = true
          console.log('✅ API WordPress connessa e funzionante')
        } else {
          this.isOnline = false
          console.warn('⚠️ API WordPress: risposta non valida')
        }
      } else {
        this.isOnline = false
        console.warn(`⚠️ API WordPress non disponibile - Status: ${response.status} ${response.statusText}`)
        
        // Log dettagliato dell'errore
        try {
          const errorText = await response.text()
          console.warn('📄 Risposta errore:', errorText)
        } catch (e) {
          console.warn('📄 Impossibile leggere risposta errore')
        }
      }
    } catch (error) {
      this.isOnline = false
      console.warn('⚠️ Impossibile connettersi all\'API WordPress:', error)
      console.warn('🔗 URL tentato:', this.baseUrl)
      console.warn('🌐 Origin corrente:', window.location.origin)
    }
  }

  /**
   * Recupera tutte le esperienze dal database WordPress
   */
  async getAllExperiences(): Promise<WordPressExperience[]> {
    const cacheKey = 'all_experiences'
    const cached = this.getCachedData(cacheKey)
    
    if (cached) {
      console.log('📦 Esperienze caricate dalla cache')
      return cached
    }

    if (!this.isOnline) {
      console.warn('🌐 API offline, uso esperienze di fallback')
      return this.getDefaultExperiences()
    }

    try {
      console.log('🔄 Caricamento esperienze da WordPress...')
      const response = await fetch(`${this.baseUrl}?action=experiences`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      // La risposta può essere sia {success: true, data: []} che direttamente un array
      if (result.success && result.data) {
        console.log(`✅ Caricate ${result.data.length} esperienze da WordPress`)
        this.setCachedData(cacheKey, result.data)
        return result.data
      } else if (Array.isArray(result.data) || Array.isArray(result)) {
        // Se result.data è array o result stesso è array
        const experiences = Array.isArray(result.data) ? result.data : result
        console.log(`✅ Caricate ${experiences.length} esperienze da WordPress`)
        this.setCachedData(cacheKey, experiences)
        return experiences
      } else {
        throw new Error(result.error || result.message || 'Errore nel recupero delle esperienze')
      }
    } catch (error) {
      console.error('❌ Errore nel recupero delle esperienze da WordPress:', error)
      this.isOnline = false
      // Fallback: ritorna esperienze di default
      return this.getDefaultExperiences()
    }
  }

  /**
   * Recupera esperienze filtrate per tipo
   */
  async getExperiencesByType(type: '3d' | 'ar' | 'hologram' | 'video' ): Promise<WordPressExperience[]> {
    const cacheKey = `experiences_${type}`
    const cached = this.getCachedData(cacheKey)
    
    if (cached) {
      console.log(`📦 Esperienze ${type} caricate dalla cache`)
      return cached
    }

    if (!this.isOnline) {
      console.warn('🌐 API offline, uso esperienze di fallback filtrate')
      const allExperiences = this.getDefaultExperiences()
      return allExperiences.filter(exp => exp.experience_type === type)
    }

    try {
      console.log(`🔄 Caricamento esperienze ${type} da WordPress...`)
      const response = await fetch(`${this.baseUrl}?action=get_experiences_by_type&experience_type=${type}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.success && result.data) {
        console.log(`✅ Caricate ${result.count} esperienze ${type} da WordPress`)
        this.setCachedData(cacheKey, result.data)
        return result.data
      } else {
        throw new Error(result.error || 'Errore nel recupero delle esperienze')
      }
    } catch (error) {
      console.error(`❌ Errore nel recupero delle esperienze ${type} da WordPress:`, error)
      // Fallback: filtra le esperienze di default
      const allExperiences = this.getDefaultExperiences()
      return allExperiences.filter(exp => exp.experience_type === type)
    }
  }

  /**
   * Recupera una singola esperienza per ID
   */
  async getExperienceById(experienceId: string): Promise<WordPressExperience | null> {
    const cacheKey = `experience_${experienceId}`
    const cached = this.getCachedData(cacheKey)
    
    if (cached) {
      console.log(`📦 Esperienza ${experienceId} caricata dalla cache`)
      return cached
    }

    if (!this.isOnline) {
      console.warn('🌐 API offline, uso esperienze di fallback')
      const defaultExp = this.getDefaultExperiences().find(exp => exp.experience_id === experienceId)
      return defaultExp || null
    }

    try {
      console.log(`🔄 Caricamento esperienza ${experienceId} da WordPress...`)
      const response = await fetch(`${this.baseUrl}?action=get_experience&experience_id=${experienceId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.success && result.data && result.data.experience) {
        console.log(`✅ Esperienza ${experienceId} caricata da WordPress`)
        this.setCachedData(cacheKey, result.data.experience)
        return result.data.experience
      } else if (result.success && result.data) {
        console.log(`✅ Esperienza ${experienceId} caricata da WordPress`)
        this.setCachedData(cacheKey, result.data)
        return result.data
      } else {
        console.warn(`⚠️ Esperienza ${experienceId} non trovata:`, result.error || result.message)
        return null
      }
    } catch (error) {
      console.error(`❌ Errore nel recupero dell'esperienza ${experienceId}:`, error)
      return null
    }
  }

  /**
   * Cerca esperienze per termine
   */
  async searchExperiences(searchTerm: string): Promise<WordPressExperience[]> {
    if (!searchTerm.trim()) {
      return this.getAllExperiences()
    }

    const cacheKey = `search_${searchTerm}`
    const cached = this.getCachedData(cacheKey)
    
    if (cached) {
      console.log(`📦 Risultati ricerca "${searchTerm}" caricati dalla cache`)
      return cached
    }

    if (!this.isOnline) {
      console.warn('🌐 API offline, uso ricerca locale')
      // Fallback: ricerca locale
      const allExperiences = await this.getAllExperiences()
      const term = searchTerm.toLowerCase()
      return allExperiences.filter(exp => 
        exp.title.toLowerCase().includes(term) ||
        exp.description.toLowerCase().includes(term) ||
        exp.experience_id.toLowerCase().includes(term) ||
        exp.model_type.toLowerCase().includes(term)
      )
    }

    try {
      console.log(`🔍 Ricerca esperienze per "${searchTerm}"...`)
      // Per ora uso filtro locale su tutte le esperienze
      const allExperiences = await this.getAllExperiences()
      const term = searchTerm.toLowerCase()
      const filtered = allExperiences.filter(exp => 
        exp.title.toLowerCase().includes(term) ||
        exp.description.toLowerCase().includes(term) ||
        exp.experience_id.toLowerCase().includes(term) ||
        exp.model_type.toLowerCase().includes(term)
      )
      
      console.log(`✅ Trovate ${filtered.length} esperienze per "${searchTerm}"`)
      this.setCachedData(cacheKey, filtered)
      return filtered
    } catch (error) {
      console.error('❌ Errore nella ricerca delle esperienze da WordPress:', error)
      // Fallback: ricerca locale
      const allExperiences = await this.getAllExperiences()
      const term = searchTerm.toLowerCase()
      return allExperiences.filter(exp => 
        exp.title.toLowerCase().includes(term) ||
        exp.description.toLowerCase().includes(term) ||
        exp.experience_id.toLowerCase().includes(term) ||
        exp.model_type.toLowerCase().includes(term)
      )
    }
  }

  /**
   * Converte un'esperienza WordPress nel formato utilizzato dall'app
   */
  convertToAppFormat(wpExperience: any): any {
    const baseConfig = {
      id: wpExperience.id,
      title: wpExperience.title,
      text: wpExperience.text || wpExperience.description,
      img: wpExperience.imageUrl || wpExperience.image_url,
      experienceType: wpExperience.type || wpExperience.experience_type,
      modelType: wpExperience.modelType || wpExperience.model_type,
      active: true
    }

    // Configurazione specifica per tipo di esperienza
    const experienceType = wpExperience.type || wpExperience.experience_type
    const modelType = wpExperience.modelType || wpExperience.model_type
    
    // Se è un'esperienza 3D ma con modelType "ologramma", configura i parametri per ologramma
    if (experienceType === '3d' && modelType === 'ologramma') {
      return {
        ...baseConfig,
        config: {
          experienceType: '3d',
          modelType: 'ologramma',
          title: wpExperience.title,
          description: wpExperience.description,
          scale: wpExperience.scale || `14 14 14`,
          position: wpExperience.position || `0 0 -8`,
          rotation: wpExperience.rotation || `0 0 0`,
          animation: 'no',
          repeat: 'no',
          text: wpExperience.text,
          customParams: {
            videoUrl: wpExperience.videoUrl || '',
            videoName: wpExperience.id || '',
            hologramType: 'video',
            chromaColor: (wpExperience.customParams && wpExperience.customParams.chromaColor) || '0.02 0.08 0.99',
            groundSize: (wpExperience.customParams && wpExperience.customParams.groundSize) || '1000 1000',
            riseHeight: (wpExperience.customParams && wpExperience.customParams.riseHeight) || '0.915',
            autoplay: (wpExperience.customParams && wpExperience.customParams.autoplay) || 'false',
            loop: (wpExperience.customParams && wpExperience.customParams.loop) || 'false',
            cameraPosition: (wpExperience.customParams && wpExperience.customParams.cameraPosition) || '0 4 6',
            ...wpExperience.customParams
          }
        }
      }
    }
    
    switch (experienceType) {
      case '3d':
        // Determina il modelType corretto - evita 'generic' se possibile
        let finalModelType = wpExperience.modelType || wpExperience.model_type;
        
        // Se il modelType è generico o undefined, per esperienze 3d usa 'generic' come default
        if (!finalModelType || finalModelType === 'generic' || finalModelType === 'undefined') {
          if (experienceType === '3d') {
            finalModelType = 'generic';
            console.log(`🔄 ModelType era '${wpExperience.modelType || wpExperience.model_type}', uso default 'generic' per esperienza 3d`);
          } else {
            // Per altri tipi di esperienza, prova a usare l'experience_id
            finalModelType = wpExperience.experience_id;
            console.log(`🔄 ModelType era '${wpExperience.modelType || wpExperience.model_type}', uso experience_id: ${finalModelType}`);
          }
        }
        
        // Crea i customParams usando il finalModelType corretto
        const customParams = this.buildCustomParamsWithModelType(wpExperience, finalModelType);
        
        // Mappa correttamente le animazioni dal database WordPress
        let listAnim = ['Scene']; // Default
        if (wpExperience.animations && Array.isArray(wpExperience.animations)) {
          // Se le animazioni sono già un array di oggetti con animation_name
          listAnim = wpExperience.animations.map((anim: any) => anim.animation_name || anim.name || anim);
        } else if (wpExperience.listAnim) {
          // Se listAnim è già presente
          if (Array.isArray(wpExperience.listAnim)) {
            listAnim = wpExperience.listAnim;
          } else if (typeof wpExperience.listAnim === 'string') {
            try {
              listAnim = JSON.parse(wpExperience.listAnim);
            } catch {
              listAnim = [wpExperience.listAnim];
            }
          }
        } else if (wpExperience.animations && typeof wpExperience.animations === 'string') {
          // Se animations è una stringa (GROUP_CONCAT dal database)
          listAnim = (wpExperience.animations as string).split(',').map(name => name.trim());
        }
        
        console.log(`🎬 Animazioni mappate per ${wpExperience.title}:`, {
          original: wpExperience.animations,
          listAnim: listAnim,
          animation: wpExperience.animation,
          repeat: wpExperience.repeat
        });
        
        return {
          ...baseConfig,
          config: {
            experienceType: '3d',
            modelType: finalModelType,
            title: wpExperience.title,
            description: wpExperience.description,
            scale: wpExperience.scale || this.buildScaleFromValues(wpExperience.scale_x, wpExperience.scale_y, wpExperience.scale_z) || `1 1 1`,
            position: wpExperience.position || this.buildPositionFromValues(wpExperience.position_x, wpExperience.position_y, wpExperience.position_z) || `0 0 0`,
            rotation: wpExperience.rotation || this.buildRotationFromValues(wpExperience.rotation_x, wpExperience.rotation_y, wpExperience.rotation_z) || `0 0 0`,
            animation: wpExperience.animation || 'no',
            repeat: wpExperience.repeat || 'no',
            listAnim: listAnim,
            text: wpExperience.text,
            modelPath: wpExperience.modelUrl || wpExperience.model_url || this.getModelPathForType(finalModelType),
            customParams: {
              ...customParams,
              // Assicura che il modelPath sia sempre presente nei customParams
              modelPath: wpExperience.modelUrl || wpExperience.model_url || this.getModelPathForType(finalModelType)
            }
          }
        }

      case 'ar':
        return {
          ...baseConfig,
          config: {
            experienceType: 'ar',
            modelType: 'genericAR',
            title: wpExperience.title,
            description: wpExperience.description,
            customParams: {
              // Configurazione AR standard come base
              targetName: wpExperience.experience_id || 'default-target',
              videoName: wpExperience.experience_id || 'default-video',
              videoPath: wpExperience.videoUrl || `https://iscag-ei.it/wp-content/materiale/immaginianimate/${wpExperience.experience_id || 'default'}.mp4`,
              cameraPosition: '0 4 10',
              lightIntensity: '1',
              imagePath: null,
              imageScale: '1 1 1',
              arType: 'video-fade',
              
              // Sovrascrive con arConfig se presente
              ...(wpExperience.arConfig && {
                targetName: wpExperience.arConfig.targetName,
                videoName: wpExperience.arConfig.targetName,
                videoPath: wpExperience.videoUrl || '',
                cameraPosition: wpExperience.arConfig.cameraPosition,
                lightIntensity: wpExperience.arConfig.lightIntensity,
                imagePath: wpExperience.arConfig.centerImageUrl,
                imageScale: wpExperience.arConfig.centerImageScale,
                arType: wpExperience.arConfig.arType,
                width: wpExperience.arConfig.width?.toString() || '1',
                height: wpExperience.arConfig.height?.toString() || '1'
              }),
              
              // Sovrascrive con customParams esistenti se presenti (priorità massima)
              ...(wpExperience.customParams && wpExperience.customParams)
            }
          }
        }

      case 'hologram':
        return {
          ...baseConfig,
          config: {
            experienceType: 'hologram',
            modelType: 'ologramma',
            title: wpExperience.title,
            description: wpExperience.description,
            customParams: wpExperience.hologramConfig ? {
              videoUrl: wpExperience.videoUrl || '',
              hologramType: wpExperience.hologramConfig.hologramType,
              chromaColor: wpExperience.hologramConfig.chromaColor,
              groundSize: wpExperience.hologramConfig.groundSize,
              riseHeight: wpExperience.hologramConfig.riseHeight,
              autoplay: wpExperience.hologramConfig.autoplay,
              loop: wpExperience.hologramConfig.loop
            } : wpExperience.customParams || {}
          }
        }

      

      case 'video':
        // Estrai URL dall'attributo dedicato o da custom params
        // Supporta sia array custom_params [{param_name,param_value},...] che oggetto customParams {url:...}
        let iframeUrl = ''
        if (typeof wpExperience?.iframe_url === 'string' && wpExperience.iframe_url) {
          iframeUrl = wpExperience.iframe_url
        } else if (wpExperience?.customParams && typeof wpExperience.customParams === 'object') {
          // Nuova API: camelCase customParams oggetto
          iframeUrl = wpExperience.customParams.url || wpExperience.customParams.iframeUrl || ''
        } else if (Array.isArray(wpExperience?.custom_params)) {
          // Vecchia API: array di parametri
          const cp = (wpExperience.custom_params || []) as Array<{param_name: string; param_value: string}>
          const urlParam = cp.find(p => (p.param_name || '').toLowerCase() === 'url')
          iframeUrl = (urlParam && urlParam.param_value) || ''
        }

        // Se esiste un URL, trattala come esperienza iframe
        if (iframeUrl) {
          return {
            ...baseConfig,
            experience_id: wpExperience.experience_id,
            config: {
              experienceType: 'iframe',
              modelType: 'generic',
              title: wpExperience.title,
              description: wpExperience.description,
              customParams: { url: iframeUrl }
            }
          }
        }

        // Altrimenti resta una normale esperienza video
        return {
          ...baseConfig,
          config: {
            experienceType: 'video',
            modelType: 'video',
            title: wpExperience.title,
            description: wpExperience.description,
            customParams: {
              videoUrl: wpExperience.video_url || '',
              imageUrl: wpExperience.image_url || ''
            }
          }
        }

      default:
        return baseConfig
    }
  }

  /**
   * Ottiene il modelPath basato sul modelType
   */
  private getModelPathForType(modelType: string): string {
    try {
      // Controlla se modelType è valido
      if (!modelType || modelType === 'undefined' || modelType === 'null') {
        console.warn(`⚠️ ModelType non valido: '${modelType}', fallback a castelOggi`);
        return 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/castel-oggi.glb';
      }
      
      // Configurazione degli assets
      const ASSETS_CONFIG = {
        models: {
          generic: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/castel-oggi.glb', // Default per generic
          castelOggi: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/castel-oggi.glb',
          castelIeri: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/castel-ieri.glb',
          tutaAmianto: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/Tuta_amianto.glb',
          bunker: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/bunker.glb',
          bleirot: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/bleirot.glb',
          casteldelmonte: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/casteldelmonte.glb',
          nuraghe: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/nuraghe.glb',
          pontebarche: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/pontebarche.glb',
          radiomarconi: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/radiomarconi.glb',
          statuatrapano: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/statuatrapano.glb',
          stazioneradio: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/stazioneradio.glb',
          carrosuponte: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/carrosuponte.glb',
          stemmaricostruire: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/stemmaricostruire.glb',
          teleferica: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/teleferica.glb',
          vscalegnobarca: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/vscalegnobarca.glb',
          idroscivolante: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/idroscivolante.glb',
          metaldetector: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/metaldetector.glb',
          aquile: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/aquile.glb',
          minatorechetrapano: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/minatorechetrapano.glb',
          mitragliatrice: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/mitragliatrice.glb',
          mongolfiera: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/mongolfiera.glb',
          palladicannone: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/palladicannone.glb',
          forno: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/forno.glb'
        }
      }
      
      if (ASSETS_CONFIG.models[modelType]) {
        console.log(`✅ ModelPath trovato per '${modelType}':`, ASSETS_CONFIG.models[modelType]);
        return ASSETS_CONFIG.models[modelType];
      } else {
        console.warn(`⚠️ ModelType '${modelType}' non trovato negli assets, fallback a castelOggi`);
        return ASSETS_CONFIG.models.castelOggi;
      }
    } catch (error) {
      console.error('❌ Errore nel recupero modelPath:', error);
      return 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/castel-oggi.glb';
    }
  }
  
  /**
   * Costruisce una stringa scale dai valori individuali
   */
  private buildScaleFromValues(x: number, y: number, z: number): string | null {
    if (x !== undefined && y !== undefined && z !== undefined) {
      return `${x} ${y} ${z}`;
    }
    return null;
  }
  
  /**
   * Costruisce una stringa position dai valori individuali
   */
  private buildPositionFromValues(x: number, y: number, z: number): string | null {
    if (x !== undefined && y !== undefined && z !== undefined) {
      return `${x} ${y} ${z}`;
    }
    return null;
  }
  
  /**
   * Costruisce una stringa rotation dai valori individuali
   */
  private buildRotationFromValues(x: number, y: number, z: number): string | null {
    if (x !== undefined && y !== undefined && z !== undefined) {
      return `${x} ${y} ${z}`;
    }
    return null;
  }
  
  /**
   * Costruisce i parametri personalizzati
   */
  private buildCustomParams(wpExperience: WordPressExperience): Record<string, any> {
    const params: Record<string, any> = {}

    // Aggiungi parametri transform base
    const scale = this.buildScaleFromValues(wpExperience.scale_x, wpExperience.scale_y, wpExperience.scale_z);
    const position = this.buildPositionFromValues(wpExperience.position_x, wpExperience.position_y, wpExperience.position_z);
    const rotation = this.buildRotationFromValues(wpExperience.rotation_x, wpExperience.rotation_y, wpExperience.rotation_z);
    
    if (scale) params.scale = scale;
    if (position) params.position = position;
    if (rotation) params.rotation = rotation;
    
    // Aggiungi parametri di animazione base
    if (wpExperience.animation) params.animation = wpExperience.animation;
    if (wpExperience.repeat_animation) params.repeat = wpExperience.repeat_animation;
    if (wpExperience.animation_text) params.text = wpExperience.animation_text;
    
    // Aggiungi modelPath se disponibile
    let modelPath = wpExperience.model_url;
    if (!modelPath) {
      // Se non c'è model_url, prova a determinare il modelType e usalo
      const modelType = wpExperience.model_type;
      if (modelType === 'generic') {
        // Per generic, prova prima i customParams, poi il fallback
        modelPath = wpExperience.custom_params?.find(p => p.param_name === 'modelPath')?.param_value || 
                   this.getModelPathForType('generic');
      } else {
        modelPath = this.getModelPathForType(modelType);
      }
    }
    
    if (modelPath) params.modelPath = modelPath;
    
    // Aggiungi parametri personalizzati dal database
    if (wpExperience.custom_params) {
      wpExperience.custom_params.forEach(param => {
        if (param.param_type === 'json') {
          try {
            params[param.param_name] = JSON.parse(param.param_value)
          } catch {
            params[param.param_name] = param.param_value
          }
        } else if (param.param_type === 'number') {
          params[param.param_name] = parseFloat(param.param_value)
        } else if (param.param_type === 'boolean') {
          params[param.param_name] = param.param_value === 'true'
        } else {
          params[param.param_name] = param.param_value
        }
      })
    }

    return params
  }

  /**
   * Costruisce i parametri personalizzati con un modelType specifico
   */
  private buildCustomParamsWithModelType(wpExperience: WordPressExperience, modelType: string): Record<string, any> {
    const params: Record<string, any> = {}

    // Aggiungi parametri transform base
    const scale = this.buildScaleFromValues(wpExperience.scale_x, wpExperience.scale_y, wpExperience.scale_z);
    const position = this.buildPositionFromValues(wpExperience.position_x, wpExperience.position_y, wpExperience.position_z);
    const rotation = this.buildRotationFromValues(wpExperience.rotation_x, wpExperience.rotation_y, wpExperience.rotation_z);
    
    if (scale) params.scale = scale;
    if (position) params.position = position;
    if (rotation) params.rotation = rotation;
    
    // Aggiungi parametri di animazione base
    if (wpExperience.animation) params.animation = wpExperience.animation;
    if (wpExperience.repeat_animation) params.repeat = wpExperience.repeat_animation;
    if (wpExperience.animation_text) params.text = wpExperience.animation_text;
    
    // Mappa correttamente le animazioni per listAnim
    if (wpExperience.animations && Array.isArray(wpExperience.animations)) {
      // Se le animazioni sono già un array di oggetti con animation_name
      params.listAnim = wpExperience.animations.map((anim: any) => anim.animation_name || anim.name || anim);
    } else if (wpExperience.animations && typeof wpExperience.animations === 'string') {
      // Se animations è una stringa (GROUP_CONCAT dal database)
      params.listAnim = (wpExperience.animations as string).split(',').map(name => name.trim());
    }
    
    // Aggiungi modelPath usando il modelType corretto
    // Per 'generic', usa il modelPath specificato nei customParams se disponibile
    let modelPath = wpExperience.model_url;
    if (!modelPath) {
      if (modelType === 'generic') {
        // Per generic, prova prima i customParams, poi il fallback
        modelPath = wpExperience.custom_params?.find(p => p.param_name === 'modelPath')?.param_value || 
                   this.getModelPathForType('generic');
      } else {
        modelPath = this.getModelPathForType(modelType);
      }
    }
    
    if (modelPath) params.modelPath = modelPath;
    
    // Aggiungi envmap se presente
    console.log('🌍 === DEBUG ENVMAP IN buildCustomParamsWithModelType ===')
    console.log('🌍 wpExperience.envmap:', wpExperience.envmap)
    console.log('🌍 typeof wpExperience.envmap:', typeof wpExperience.envmap)
    
    if (wpExperience.envmap) {
      // Se l'envmap è un oggetto, convertilo in stringa JSON per i customParams
      if (typeof wpExperience.envmap === 'object') {
        params.envmap = JSON.stringify(wpExperience.envmap)
        console.log('✅ Envmap aggiunto come JSON string:', params.envmap)
      } else if (typeof wpExperience.envmap === 'string') {
        params.envmap = wpExperience.envmap
        console.log('✅ Envmap aggiunto come stringa:', params.envmap)
      }
    } else {
      console.log('❌ Nessun envmap trovato in wpExperience')
    }
    
    // Aggiungi parametri personalizzati dal database
    if (wpExperience.custom_params) {
      wpExperience.custom_params.forEach(param => {
        if (param.param_type === 'json') {
          try {
            params[param.param_name] = JSON.parse(param.param_value)
          } catch {
            params[param.param_name] = param.param_value
          }
        } else if (param.param_type === 'number') {
          params[param.param_name] = parseFloat(param.param_value)
        } else if (param.param_type === 'boolean') {
          params[param.param_name] = param.param_value === 'true'
        } else {
          params[param.param_name] = param.param_value
        }
      })
    }

    return params
  }

  /**
   * Health check dell'API WordPress
   */
  async healthCheck(): Promise<WordPressHealthResponse | null> {
    if (!this.isOnline) {
      console.warn('🌐 API offline, health check non disponibile')
      return null
    }

    try {
      console.log('🏥 Health check API WordPress...')
      const response = await fetch(`${this.baseUrl}?action=experiences`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.success && result.data) {
        console.log('✅ Health check completato:', result)
        // Crea un oggetto health response compatibile
        const healthResponse: WordPressHealthResponse = {
          status: 'online',
          database: 'connected',
          total_experiences: result.data.length || 0,
          type_counts: [],
          timestamp: new Date().toISOString(),
          wordpress_version: 'unknown',
          plugin_version: '1.0.0'
        }
        return healthResponse
      } else {
        console.warn('⚠️ Health check fallito: risposta non valida')
        return null
      }
    } catch (error) {
      console.error('❌ Errore health check:', error)
      this.isOnline = false
      return null
    }
  }

  /**
   * Forza il test della connessione
   */
  async forceConnectionTest(): Promise<boolean> {
    await this.testConnection()
    return this.isOnline
  }

  /**
   * Test diagnostico completo
   */
  async runDiagnostics(): Promise<{
    baseUrl: string
    currentOrigin: string
    isOnline: boolean
    lastError?: string
    testResult?: any
  }> {
    console.log('🔍 Diagnostica API WordPress...')
    console.log('🔗 Base URL configurato:', this.baseUrl)
    console.log('🌐 Origin corrente:', window.location.origin)
    console.log('📱 User Agent:', navigator.userAgent)
    
    try {
      const response = await fetch(`${this.baseUrl}?action=experiences`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      const result: {
        baseUrl: string
        currentOrigin: string
        isOnline: boolean
        lastError?: string
        testResult?: any
      } = {
        baseUrl: this.baseUrl,
        currentOrigin: window.location.origin,
        isOnline: response.ok,
        testResult: response.ok ? await response.json() : null
      }

      if (!response.ok) {
        try {
          const errorText = await response.text()
          result.lastError = `HTTP ${response.status}: ${errorText}`
        } catch (e) {
          result.lastError = `HTTP ${response.status}: ${response.statusText}`
        }
      }

      console.log('📊 Risultato diagnostica:', result)
      return result
      
    } catch (error) {
      const result: {
        baseUrl: string
        currentOrigin: string
        isOnline: boolean
        lastError?: string
        testResult?: any
      } = {
        baseUrl: this.baseUrl,
        currentOrigin: window.location.origin,
        isOnline: false,
        lastError: error.message
      }
      
      console.log('❌ Errore diagnostica:', result)
      return result
    }
  }

  /**
   * Gestione cache locale
   */
  private getCachedData(key: string): any | null {
    const cached = this.cache.get(key)
    if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
      return cached.data
    }
    return null
  }

  private setCachedData(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    })
  }

  /**
   * Pulisce la cache
   */
  clearCache(): void {
    this.cache.clear()
    console.log('🧹 Cache pulita')
  }

  /**
   * Imposta la durata della cache
   */
  setCacheDuration(duration: number): void {
    this.cacheDuration = duration
    console.log(`⏰ Durata cache impostata a ${duration}ms`)
  }

  /**
   * Ottiene lo stato di connessione
   */
  getConnectionStatus(): boolean {
    return this.isOnline
  }

  /**
   * Esperienze di fallback se WordPress non è disponibile
   */
  private getDefaultExperiences(): WordPressExperience[] {
    console.log('🔄 Caricamento esperienze di fallback...')
    return [
      {
        id: 1,
        experience_id: 'castelOggi',
        title: 'Castel Oggi',
        description: 'Modello 3D del Castel Oggi con animazioni e interazioni.',
        experience_type: '3d',
        model_type: 'castelOggi',
        scale_x: 0.7,
        scale_y: 0.7,
        scale_z: 0.7,
        position_x: 0,
        position_y: 0,
        position_z: -15,
        rotation_x: 0,
        rotation_y: 90,
        rotation_z: 0,
        animation: 'no',
        repeat_animation: 'yes',
        animation_text: 'Avvia Animazione',
        model_url: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/castel-oggi.glb',
        image_url: 'https://iscag-ei.it/wp-content/materiale/cards-copertina/castelOggi.png',
        video_url: '',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        animations: [
          { id: 1, experience_id: 'castelOggi', animation_name: 'Scene', animation_order: 1 },
          { id: 2, experience_id: 'castelOggi', animation_name: 'hiphop', animation_order: 2 },
          { id: 3, experience_id: 'castelOggi', animation_name: 'chicken', animation_order: 3 }
        ]
      },
      {
        id: 2,
        experience_id: 'castelIeri',
        title: 'Castel Ieri',
        description: 'Modello 3D del Castel Ieri con animazioni e interazioni.',
        experience_type: '3d',
        model_type: 'castelIeri',
        scale_x: 5,
        scale_y: 5,
        scale_z: 5,
        position_x: 0,
        position_y: 0,
        position_z: -15,
        rotation_x: 0,
        rotation_y: 0,
        rotation_z: 0,
        animation: 'yes',
        repeat_animation: 'yes',
        animation_text: 'Fai volare le Aquile',
        model_url: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/castel-ieri.glb',
        image_url: 'https://iscag-ei.it/wp-content/materiale/cards-copertina/castelIeri.png',
        video_url: '',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        animations: [
          { id: 1, experience_id: 'castelIeri', animation_name: 'Animation', animation_order: 1 }
        ]
      },
      {
        id: 3,
        experience_id: 'palladicannone',
        title: 'Palladi Cannone',
        description: 'Modello 3D della Palladi Cannone con animazioni e interazioni.',
        experience_type: '3d',
        model_type: 'palladicannone',
        scale_x: 5,
        scale_y: 5,
        scale_z: 5,
        position_x: 0,
        position_y: 0,
        position_z: -15,
        rotation_x: 0,
        rotation_y: 0,
        rotation_z: 0,
        animation: 'yes',
        repeat_animation: 'no',
        animation_text: 'Fai uscire la palla di cannone',
        model_url: 'https://iscag-ei.it/wp-content/materiale/animazioni-3d/palladi-cannone.glb',
        image_url: 'https://iscag-ei.it/wp-content/materiale/cards-copertina/palladicannone.png',
        video_url: '',
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        animations: [
          { id: 1, experience_id: 'palladicannone', animation_name: 'SphereAction', animation_order: 1 }
        ]
      }
    ]
  }
}

// Esporta un'istanza singleton
export const wordpressAPI = new WordPressAPI()

// Esporta anche la classe per testing
export { WordPressAPI }
